import { useLocation } from '@reach/router';
import { navigate } from 'gatsby';
import React from 'react';
import styled from 'styled-components';

import data from '../../../data/data.json';
import Section from '../../common/Section';
import Button from '../../common/ui/Button';
import CtaLink from '../../common/ui/CtaLink';
import Check from '../../icons/checkmark.svg';

const Prices = ({
  defaultCountry = 'us',
  showLearnMore = true,
  showCurrencyPrefix = true,
  bookDemoButtonComponent
}) => {
  const { plans } = data;

  const location = useLocation();
  const countriesUrlPath = ['us/', 'ca/', 'za/', 'au/', 'nz/', 'eu/', 'uk/'];

  const visiblePlans = plans.filter((plan) => plan.showOnPricePage);
  const path = countriesUrlPath.find(
    (country) => location.pathname.slice(9) === country
  );

  let country = path || defaultCountry;
  country = country.replace('/', '');

  const GetStartedButtonWithNavigate = ({ to, children, countryPath }) => {
    const handleClick = () => {
      navigate(to, { state: { country: countryPath } });
    };
    return (
      <GetStartedButton onClick={handleClick}>{children}</GetStartedButton>
    );
  };

  return (
    <StyledSection>
      <Wrapper>
        {visiblePlans.map((plan) => {
          const monthlyPrice = plan.priceMonthly[country]?.price;
          const currencyIcon = plan.priceMonthly[country]?.currencyIcon;
          const currencyPrefix = plan.priceMonthly[country]?.currencyPrefix;

          const description = plan.description[country]?.description;
          const link = `/signup/${plan.slug}/${country}`;

          return (
            <PriceWrapper key={plan.slug}>
              <PriceBox color={plan.color}>
                <Title>{plan.name}</Title>
                <Description>{description}</Description>
                {plan.name === 'Enterprise' ? (
                  <>
                    <EnterpriseLabel>Let&apos;s talk!</EnterpriseLabel>
                    <Description>Custom plans to fit your needs</Description>
                    {bookDemoButtonComponent || (
                      <GetStartedButton to="/contact">
                        Contact Sales
                      </GetStartedButton>
                    )}
                  </>
                ) : (
                  <>
                    <StyledPricePerMonth>
                      <PricePrefix>
                        {showCurrencyPrefix && currencyPrefix}
                      </PricePrefix>
                      <Price>
                        {currencyIcon}
                        {monthlyPrice} <Time>/ month</Time>
                      </Price>
                    </StyledPricePerMonth>
                    <StyledPricePerMonth>
                      {bookDemoButtonComponent || (
                        <GetStartedButtonWithNavigate
                          to={link}
                          countryPath={country}
                        >
                          Get Started
                        </GetStartedButtonWithNavigate>
                      )}
                    </StyledPricePerMonth>
                    <Fee>
                      {plan.transactionFee} transaction fee. {plan.setupFee}
                    </Fee>
                    <Features />
                  </>
                )}
              </PriceBox>
              {showLearnMore && (
                <LearnMoreLink to={plan.learnMoreLink} color="var(--blue-base)">
                  {plan.learnMoreText}
                </LearnMoreLink>
              )}
            </PriceWrapper>
          );
        })}
      </Wrapper>
    </StyledSection>
  );
};

const Features = () => (
  <FeaturesWrapper>
    <FeatureLabel>Includes all features</FeatureLabel>
    <ul>
      <FeatureItem>
        <StyledCheck /> POS
      </FeatureItem>
      <FeatureItem>
        <StyledCheck /> Clubs &amp; Subscriptions
      </FeatureItem>
      <FeatureItem>
        <StyledCheck /> Ecommerce
      </FeatureItem>
      <FeatureItem>
        <StyledCheck /> CRM
      </FeatureItem>
      <FeatureItem>
        <StyledCheck /> Reservations
      </FeatureItem>
    </ul>
  </FeaturesWrapper>
);

const StyledPricePerMonth = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledSection = styled(Section)`
  margin-bottom: 125px;
`;

const Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  justify-content: center;
`;

const PriceWrapper = styled.div`
  width: 100%;
  margin-bottom: 60px;

  ${({ theme }) => `
  @media ${theme.mediumUp} {
    width: 50%;
    padding: 0 16px;
  }
    @media ${theme.xLargeUp} {
      width: 25%;
      padding: 0 16px;
    }
`}
`;

const PriceBox = styled.div`
  background: white;
  border-radius: 8px;
  text-align: center;
  border: 1px solid rgb(220, 223, 232);
  box-shadow: 1px 2px 8px rgba(7, 32, 128, 0.08);
  overflow: hidden;
  height: 100%;
  &:before {
    content: '';
    height: 10px;
    display: block;
    width: 100%;
    background: ${({ color }) => color};
    margin-bottom: 40px;
  }
`;

const Title = styled.h4`
  color: var(--blue-dark);
  font-size: var(--heading-size-xs);
  font-weight: var(--font-weight-bold);
  margin-bottom: 10px;
`;

const Description = styled.p`
  max-width: 270px;
  margin: 0 auto 20px;
`;

const Price = styled.p`
  color: var(--blue-dark);
  font-size: var(--heading-size-lg);
  font-weight: var(--font-weight-black);
  margin-bottom: 10px;
`;

const PricePrefix = styled.p`
  font-weight: var(--font-weight-base);
  font-size: var(--font-size-base);
  margin-bottom: 20px;
  margin-right: 10px;
`;

const Time = styled.span`
  font-weight: var(--font-weight-base);
  font-size: var(--font-size-base);
`;

const GetStartedButton = styled(Button)`
  margin-bottom: 40px;
  height: 46px;
`;

const Fee = styled.p`
  font-size: 15px;
  margin-bottom: 20px;
`;

const FeaturesWrapper = styled.div`
  border-top: 1px solid rgb(220, 223, 232);
  padding: 25px 30px 5px;
  text-align: left;
`;

const FeatureLabel = styled.p`
  font-weight: var(--font-weight-semibold);
  margin-bottom: 15px;
`;

const FeatureItem = styled.li`
  margin-bottom: 10px;
  display: flex;
  align-items: center;
`;

const StyledCheck = styled(Check)`
  width: 12px;
  margin-right: 10px;
`;

const EnterpriseLabel = styled.h4`
  font-size: var(--heading-size-base);
  font-weight: var(--font-weight-bold);
  color: var(--blue-dark);
  margin: -5px 0 10px;
`;

const LearnMoreLink = styled(CtaLink)`
  margin-top: 30px;
  text-align: center;
  display: block;
`;

export default Prices;
